/**
 * RatingScorecardBlock - Display a rating scorecard with dimensions
 */

import React from 'react'
import { RatingScorecardBlock as RatingScorecardBlockType } from '../../../../lib/cockpit/schemas'

interface RatingScorecardBlockProps {
  data: RatingScorecardBlockType['data']
  blockId: string
  compact?: boolean
}

function RatingScorecardBlock({ data, blockId: _blockId, compact }: RatingScorecardBlockProps) {
  const { subject, dimensions, overall_score, overall_max, grade } = data

  const overallPercentage = overall_max ? (overall_score / overall_max) * 100 : overall_score

  if (compact) {
    return (
      <div className="flex items-center justify-between">
        <span className="font-medium text-gray-900 dark:text-white">{subject}</span>
        <div className="flex items-center gap-2">
          {grade && (
            <span className={`text-lg font-bold ${getGradeColor(grade)}`}>{grade}</span>
          )}
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {overall_score}{overall_max ? `/${overall_max}` : ''}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with overall score */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white text-lg">{subject}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Overall Rating</p>
        </div>
        <div className="text-right">
          {grade && (
            <div className={`text-3xl font-bold ${getGradeColor(grade)}`}>{grade}</div>
          )}
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {overall_score}{overall_max ? ` / ${overall_max}` : ''}
          </div>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${getScoreBarColor(overallPercentage)}`}
          style={{ width: `${Math.min(100, overallPercentage)}%` }}
        />
      </div>

      {/* Individual dimensions */}
      {dimensions && dimensions.length > 0 && (
        <div className="space-y-3">
          {dimensions.map((dim, idx) => {
            const dimPercentage = dim.max_score ? (dim.score / dim.max_score) * 100 : dim.score
            return (
              <div key={dim.id || idx} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">{dim.name}</span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {dim.score}{dim.max_score ? ` / ${dim.max_score}` : ''}
                    {dim.weight && <span className="text-xs text-gray-400 ml-1">({dim.weight}x)</span>}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${getScoreBarColor(dimPercentage)}`}
                    style={{ width: `${Math.min(100, dimPercentage)}%` }}
                  />
                </div>
                {dim.rationale && (
                  <p className="text-xs text-gray-500 dark:text-gray-500">{dim.rationale}</p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function getGradeColor(grade: string): string {
  const g = grade.toUpperCase()
  if (g.startsWith('A')) return 'text-green-600 dark:text-green-400'
  if (g.startsWith('B')) return 'text-blue-600 dark:text-blue-400'
  if (g.startsWith('C')) return 'text-yellow-600 dark:text-yellow-400'
  if (g.startsWith('D')) return 'text-orange-600 dark:text-orange-400'
  return 'text-red-600 dark:text-red-400'
}

function getScoreBarColor(percentage: number): string {
  if (percentage >= 80) return 'bg-green-500'
  if (percentage >= 60) return 'bg-blue-500'
  if (percentage >= 40) return 'bg-yellow-500'
  if (percentage >= 20) return 'bg-orange-500'
  return 'bg-red-500'
}

export default RatingScorecardBlock

